import { useNavigate } from "@tanstack/react-router";
import { BellIcon, XIcon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useWorldLivenessRollups } from "~/neucleos/useWorldLiveness";
import { useRootSessionThreadInputs } from "~/rootSessionStripContainer";
import { useWorldUnseenSettleRollups } from "~/neucleos/useWorldUnseenSettle";
import {
  enterLocal,
  enterWorld,
  rememberWorldRoom,
  useNeucleosWorldStore,
} from "~/neucleos/worldStore";
import { LOCAL_WORLD_DOOR, worldDisplayName } from "~/neucleos/worldSelection";
import { openWorldNotice } from "~/neucleos/openWorldNotice";
import {
  worldAttentionNotices,
  type CrmReminder,
  type WorldAttentionNotice,
  type NotificationDestination,
} from "~/neucleos/notifications";
import { useNotificationActivity } from "~/neucleos/useNotificationActivity";
import {
  markNotifications,
  useNotificationSnapshots,
  type NotificationRead,
  type NotificationState,
} from "~/neucleos/notificationObserver";
import {
  captureNotificationReads,
  useNotificationSources,
} from "~/neucleos/useNotificationSources";
import {
  Popover,
  PopoverTrigger,
  PopoverPopup,
  PopoverTitle,
  PopoverDescription,
  PopoverClose,
} from "./ui/popover";
import { toastManager } from "./ui/toast";

const actionClass =
  "rounded px-2 py-1 text-xs text-primary hover:bg-accent focus-visible:outline-2 disabled:opacity-50";

function reminderDate(row: CrmReminder): string {
  const at = row.state === "delivered" ? row.deliveredAt : row.remindAt;
  const state = row.state.charAt(0).toUpperCase() + row.state.slice(1);
  return `${state}${at ? ` · ${new Date(at).toLocaleString()}` : ""}${row.completedAt ? " · Follow-up completed" : ""}`;
}

/** A single shell door; decisions and reminder records stay in their owning World. */
export function DeskNotifications() {
  const navigate = useNavigate();
  const worlds = useNeucleosWorldStore((state) => state.worlds);
  const signedIn = useNeucleosWorldStore((state) => state.signedIn);
  const activeWorldKey = useNeucleosWorldStore((state) => state.activeWorldKey);
  const localSelected = useNeucleosWorldStore((state) => state.selection?.status === "local");
  const liveness = useWorldLivenessRollups();
  const unseen = useWorldUnseenSettleRollups();
  const threads = useRootSessionThreadInputs();
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const feeds = useNotificationSnapshots(signedIn ? worlds.map((world) => world.worldKey) : []);
  const sources = useNotificationSources(
    signedIn ? worlds.map((world) => world.worldKey) : [],
    open,
    history,
    feeds,
  );
  const rawActivity = worldAttentionNotices(
    signedIn ? worlds : [],
    activeWorldKey,
    localSelected,
    liveness,
    unseen,
    threads,
  );
  const activitySource = useNotificationActivity(rawActivity);
  const stateOf = (
    worldKey: string | null,
    kind: NotificationRead["kind"],
    id: string,
    revision: string,
  ): NotificationState => {
    if (worldKey === null) {
      const receipt = activitySource.localReads[id];
      return receipt?.revision === revision ? receipt.state : "unread";
    }
    const value = feeds.find((row) => row.worldKey === worldKey)?.value;
    if (!value) return "read"; // wait for the person's authoritative receipt before showing a backlog
    if (kind === "activity") {
      const receipt = value.activityReads.find((row) => row.id === id);
      return receipt?.revision === revision ? receipt.state : "unread";
    }
    const item = (kind === "gate" ? value.gates : value.reminders)?.find((row) => row.id === id);
    return item?.revision === revision ? item.readState : "unread";
  };
  const activity = activitySource.rows.filter(
    (row) =>
      (row.worldKey === null ||
        feeds.some((feed) => feed.worldKey === row.worldKey && feed.value)) &&
      (history || stateOf(row.worldKey, "activity", row.id, row.revision) === "unread"),
  );
  const gateRows = history
    ? sources.rows.flatMap((source) =>
        source.gates.ok
          ? source.gates.rows.map((row) => ({
              id: row.gateInstanceKey,
              revision: row.targetDigest,
              label: row.title,
              readState: row.notificationReadState ?? "unread",
              worldKey: source.worldKey,
            }))
          : [],
      )
    : feeds.flatMap((feed) =>
        (feed.value?.gates ?? [])
          .filter((row) => history || row.readState === "unread")
          .map((row) => ({ ...row, worldKey: feed.worldKey })),
      );
  const visibleReminder = (worldKey: string, row: CrmReminder) =>
    history ||
    (row.state === "delivered" &&
      !row.completedAt &&
      (feeds
        .find((feed) => feed.worldKey === worldKey)
        ?.value?.reminders?.find((item) => item.id === row.reminderId)?.readState ??
        row.notificationReadState) === "unread");
  const reminderSources = sources.rows.filter(
    (source) =>
      !source.reminders.ok ||
      !source.reminders.result.isDone ||
      source.reminders.result.page.some((row) => visibleReminder(source.worldKey, row)),
  );
  const unavailable = feeds.some(
    (feed) => feed.error || (feed.value && (!feed.value.gates || !feed.value.reminders)),
  );
  const hasActivity =
    activitySource.rows.some(
      (row) => stateOf(row.worldKey, "activity", row.id, row.revision) === "unread",
    ) ||
    feeds.some(
      (feed) =>
        (feed.value?.unreadGateCount ?? 0) > 0 ||
        [...(feed.value?.gates ?? []), ...(feed.value?.reminders ?? [])].some(
          (row) => row.readState === "unread",
        ),
    );
  const mark = async (worldKey: string | null, rows: NotificationRead[]) => {
    setSaving(true);
    setError(null);
    try {
      if (worldKey === null) activitySource.markLocal(rows);
      else {
        const feed = feeds.find((row) => row.worldKey === worldKey)?.value;
        if (!feed) throw new Error("Notifications are still loading. Try again.");
        await markNotifications(worldKey, feed.actorId, rows);
        sources.refresh();
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Read state could not be saved.");
    } finally {
      setSaving(false);
    }
  };
  const markAll = async () => {
    setSaving(true);
    setError(null);
    try {
      // Capture all Worlds before the first write. Stream updates during the
      // batches cannot enlarge the set this gesture acknowledges.
      const captured = [];
      for (const feed of feeds) {
        if (!feed.value)
          throw new Error(feed.error ?? "Notifications are still loading. Try again.");
        const rows: NotificationRead[] = [
          ...(await captureNotificationReads(feed.worldKey)),
          ...activitySource.rows
            .filter((row) => row.worldKey === feed.worldKey)
            .map((row): NotificationRead => ({
              kind: "activity",
              id: row.id,
              revision: row.revision,
              state: "read",
            })),
        ];
        captured.push({ worldKey: feed.worldKey, actorId: feed.value.actorId, rows });
      }
      activitySource.markLocal(
        activitySource.rows
          .filter((row) => row.worldKey === null)
          .map((row) => ({ kind: "activity", id: row.id, revision: row.revision, state: "read" })),
      );
      for (const { worldKey, actorId, rows } of captured)
        await markNotifications(worldKey, actorId, rows);
      sources.refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Read state could not be saved.");
    } finally {
      setSaving(false);
    }
  };
  const readButtons = (
    worldKey: string | null,
    kind: NotificationRead["kind"],
    id: string,
    revision: string,
  ) => (
    <div className="flex gap-1">
      <button
        className={actionClass}
        disabled={saving}
        onClick={() =>
          void mark(worldKey, [{ kind, id, revision, state: history ? "unread" : "read" }])
        }
      >
        {history ? "Mark unread" : "Mark read"}
      </button>
      {!history && (
        <button
          className={actionClass}
          disabled={saving}
          onClick={() => void mark(worldKey, [{ kind, id, revision, state: "dismissed" }])}
        >
          Dismiss
        </button>
      )}
    </div>
  );
  const observed = useRef(new Map<string, Set<string>>());
  useEffect(() => {
    const groups = feeds
      .filter((feed) => feed.value)
      .map((feed) => ({
        key: `${feed.worldKey}:${feed.value!.actorId}`,
        items: [...(feed.value!.gates ?? []), ...(feed.value!.reminders ?? [])]
          .filter((row) => row.readState === "unread")
          .map((row) => ({
            key: `${row.id}:${row.revision}`,
            label: `${row.label} — ${worldDisplayName(worlds, feed.worldKey)}`,
          })),
      }));
    for (const worldKey of new Set(activitySource.rows.map((row) => row.worldKey))) {
      const feed = feeds.find((row) => row.worldKey === worldKey)?.value;
      if (worldKey !== null && !feed) continue;
      groups.push({
        key: `activity:${worldKey ?? "local"}:${feed?.actorId ?? "local"}`,
        items: activitySource.rows
          .filter((row) => row.worldKey === worldKey)
          .filter((row) => stateOf(row.worldKey, "activity", row.id, row.revision) === "unread")
          .map((row) => ({
            key: `${row.key}:${row.revision}`,
            label: `${row.label} — ${row.place}`,
          })),
      });
    }
    const arrivals: string[] = [];
    for (const group of groups) {
      const prior = observed.current.get(group.key);
      if (prior)
        for (const item of group.items) if (!prior.has(item.key)) arrivals.push(item.label);
      observed.current.set(
        group.key,
        new Set([...(prior ?? []), ...group.items.map((item) => item.key)]),
      );
    }
    if (!open && arrivals.length)
      toastManager.add({
        type: "info",
        title: "New notification",
        description: arrivals[0],
        timeout: 8000,
        actionProps: { children: "Open", onClick: () => setOpen(true) },
        data: { hideCopyButton: true },
      });
  }, [feeds, activitySource.rows, open, worlds]);

  const changeOpen = (value: boolean) => {
    setOpen(value);
    if (!value) {
      setHistory(false);
      setError(null);
    }
  };

  // W-a67b0503: an activity notice opens the session it names (the agent
  // strip's own door); one that names none enters its World as before.
  const openActivity = (item: WorldAttentionNotice) => {
    const subject = item.subject;
    const openThread = (at: NonNullable<WorldAttentionNotice["subject"]>) =>
      void navigate({
        to: "/$environmentId/$threadId",
        params: { environmentId: at.environmentId, threadId: at.threadId },
      } as never);
    if (item.worldKey === null) {
      if (subject && localSelected) openThread(subject);
      else {
        // Codex P2: from a World, Local opens AT the named session, never at
        // whichever Local room was last remembered.
        if (subject)
          rememberWorldRoom(LOCAL_WORLD_DOOR, `/${subject.environmentId}/${subject.threadId}`);
        enterLocal();
      }
      return;
    }
    if (!subject) {
      enterWorld(item.worldKey);
      return;
    }
    if (subject.sessionId !== null) {
      if (item.worldKey !== activeWorldKey)
        enterWorld(item.worldKey, { sessionId: subject.sessionId });
      else
        void navigate({
          to: "/session/$sessionId",
          params: { sessionId: subject.sessionId },
        } as never);
      return;
    }
    if (item.worldKey !== activeWorldKey) enterWorld(item.worldKey);
    else openThread(subject);
  };

  const openDestination = async (
    worldKey: string,
    destination: NotificationDestination,
    notification?: NotificationRead,
  ) => {
    if (opening) return;
    setOpening(true);
    setError(null);
    try {
      await openWorldNotice(worldKey, destination, navigate);
      if (notification) await mark(worldKey, [notification]);
      changeOpen(false);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The notification could not be opened.");
    } finally {
      setOpening(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger
        aria-label={
          unavailable
            ? "Notifications — some sources unavailable"
            : hasActivity
              ? "Notifications — items need your attention"
              : "Notifications"
        }
        className="ncl-ticker-glass relative flex size-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
        data-shell-utility="notifications"
      >
        <BellIcon className="size-4" aria-hidden="true" />
        {hasActivity ? (
          <span
            className="absolute right-0 top-0 size-2 rounded-full bg-primary ring-2 ring-background"
            aria-hidden="true"
          />
        ) : null}
        {unavailable ? (
          <span className="absolute -right-1 -top-1 text-xs text-warning" aria-hidden="true">
            !
          </span>
        ) : null}
      </PopoverTrigger>
      <PopoverPopup
        align="end"
        sideOffset={10}
        className="w-[min(420px,calc(100vw-24px))]"
        viewportClassName="max-h-[min(70vh,640px)] overflow-y-auto"
      >
        <div className="mb-3 flex items-center justify-between gap-3">
          <PopoverTitle>Notifications</PopoverTitle>
          <div className="flex items-center gap-1">
            <button
              className={actionClass}
              disabled={saving || !hasActivity}
              onClick={() => void markAll()}
            >
              Mark all read
            </button>
            <button className={actionClass} disabled={sources.loading} onClick={sources.refresh}>
              Refresh
            </button>
            <PopoverClose aria-label="Close notifications" className={actionClass}>
              <XIcon className="size-4" />
            </PopoverClose>
          </div>
        </div>
        <PopoverDescription className="mb-4">
          Across your Worlds. Open an item to act in its World.
        </PopoverDescription>
        {error ? (
          <p role="alert" className="mb-3 text-sm text-warning">
            {error}
          </p>
        ) : null}
        {sources.loading ? (
          <p role="status" className="mb-3 text-xs text-muted-foreground">
            Checking notifications…
          </p>
        ) : null}
        <section aria-label="World activity" className="space-y-2">
          <h3 className="text-sm font-medium">World activity</h3>
          {activity.length === 0 ? (
            <p className="text-xs text-muted-foreground">No session updates need your attention.</p>
          ) : (
            activity.map((item) => (
              <article key={item.key}>
                <button
                  className="block w-full rounded-md p-2 text-left hover:bg-accent"
                  onClick={async () => {
                    await mark(item.worldKey, [
                      { kind: "activity", id: item.id, revision: item.revision, state: "read" },
                    ]);
                    changeOpen(false);
                    openActivity(item);
                  }}
                >
                  <span className="block text-sm">{item.label}</span>
                  <span className="block text-xs text-muted-foreground">
                    {item.place} · {item.subject ? "Open session" : "Open World"}
                  </span>
                </button>
                {readButtons(item.worldKey, "activity", item.id, item.revision)}
              </article>
            ))
          )}
        </section>
        <section aria-label="Decisions" className="mt-4 space-y-2 border-t border-border pt-3">
          <h3 className="text-sm font-medium">Decisions</h3>
          {gateRows.map((row) => (
            <article key={`${row.worldKey}:${row.id}`}>
              <button
                className="block w-full rounded-md p-2 text-left hover:bg-accent"
                disabled={opening}
                // Codex P2: the Inbox marks this read when it opens the
                // request; a request no longer waiting is not marked read.
                onClick={() =>
                  void openDestination(row.worldKey, { kind: "decisions", id: row.id })
                }
              >
                <span className="block text-sm">{row.label}</span>
                <span className="block text-xs text-muted-foreground">
                  {worldDisplayName(worlds, row.worldKey)} · Open decision
                </span>
              </button>
              {readButtons(row.worldKey, "gate", row.id, row.revision)}
            </article>
          ))}
          {gateRows.length === 0 && (
            <p className="text-xs text-muted-foreground">No new decisions.</p>
          )}
          {feeds
            .filter((feed) => feed.error)
            .map((feed) => (
              <p key={feed.worldKey} className="text-xs text-warning">
                {worldDisplayName(worlds, feed.worldKey)}: {feed.error}
              </p>
            ))}
        </section>
        <section aria-label="CRM reminders" className="mt-4 space-y-3 border-t border-border pt-3">
          <h3 className="text-sm font-medium">Reminders</h3>
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={history}
              onChange={(event) => setHistory(event.target.checked)}
            />
            Include read notifications and reminder history
          </label>
          {signedIn && !sources.loading && reminderSources.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              {history ? "No reminder history." : "No new follow-up reminders."}
            </p>
          ) : null}
          {reminderSources.map((source) => (
            <div key={source.worldKey} className="space-y-2">
              <h4 className="text-xs font-medium">{worldDisplayName(worlds, source.worldKey)}</h4>
              {!source.reminders.ok ? (
                <p className="text-xs text-warning">{source.reminders.reason}</p>
              ) : (
                <>
                  {source.reminders.result.page
                    .filter((row) => visibleReminder(source.worldKey, row))
                    .map((row) => (
                      <article
                        key={row.reminderId}
                        className="space-y-1 rounded-md border border-border p-2"
                      >
                        <p className="text-sm font-medium">{row.targetName}</p>
                        <p className="whitespace-pre-wrap text-sm">{row.body}</p>
                        <p className="text-xs text-muted-foreground">
                          {reminderDate(row)}
                          {row.reason ? ` · ${row.reason}` : ""}
                        </p>
                        <div className="flex gap-2">
                          <button
                            className={actionClass}
                            disabled={opening || (!row.contactId && !row.companyId)}
                            onClick={() =>
                              void openDestination(
                                source.worldKey,
                                row.contactId
                                  ? { kind: "contact", id: row.contactId }
                                  : { kind: "company", id: row.companyId! },
                                (() => {
                                  const item = row.notificationRevision
                                    ? { id: row.reminderId, revision: row.notificationRevision }
                                    : feeds
                                        .find((feed) => feed.worldKey === source.worldKey)
                                        ?.value?.reminders?.find(
                                          (item) => item.id === row.reminderId,
                                        );
                                  return item
                                    ? {
                                        kind: "reminder" as const,
                                        id: item.id,
                                        revision: item.revision,
                                        state: "read" as const,
                                      }
                                    : undefined;
                                })(),
                              )
                            }
                          >
                            Open {row.contactId ? "contact" : "company"}
                          </button>
                          {(() => {
                            const item = row.notificationRevision
                              ? { id: row.reminderId, revision: row.notificationRevision }
                              : feeds
                                  .find((feed) => feed.worldKey === source.worldKey)
                                  ?.value?.reminders?.find((item) => item.id === row.reminderId);
                            return item
                              ? readButtons(source.worldKey, "reminder", item.id, item.revision)
                              : null;
                          })()}
                        </div>
                      </article>
                    ))}
                  {!source.reminders.result.page.some((row) =>
                    visibleReminder(source.worldKey, row),
                  ) ? (
                    <p className="text-xs text-muted-foreground">
                      {source.reminders.result.isDone
                        ? history
                          ? "No reminder history."
                          : "No new follow-up reminders."
                        : "No active reminders on this page. More are available below."}
                    </p>
                  ) : null}
                  {!source.reminders.result.isDone ? (
                    <button
                      className={actionClass}
                      disabled={sources.morePending.has(source.worldKey)}
                      onClick={() =>
                        void sources
                          .more(source.worldKey)
                          .catch(() => setError("More reminders could not be read. Try again."))
                      }
                    >
                      More reminders · {worldDisplayName(worlds, source.worldKey)}
                    </button>
                  ) : null}
                </>
              )}
            </div>
          ))}
          {!signedIn ? (
            <p className="text-xs text-muted-foreground">
              Sign in to Neucleos for World decisions and reminders.
            </p>
          ) : null}
        </section>
      </PopoverPopup>
    </Popover>
  );
}
